import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Settings, Save, Loader2, Info } from "lucide-react";

/**
 * Program Config — the single source of truth for how the Refer & Earn program
 * behaves in production. Every field has an inline hint so operators can make
 * informed decisions without reading the source. Sections are grouped by the
 * lever they control (money, timing, automation, experiments).
 */
const SECTIONS = [
  {
    title: "Rewards",
    description:
      "How much the referrer earns per successful friend, broken out by MYDAWA category. These are paid straight to the referrer's M-Pesa after the review window.",
    fields: [
      { k: "reward_otc", label: "OTC reward", type: "number",
        hint: "KES paid to the referrer when the friend's first qualifying order is OTC (over-the-counter) items. Typical: 150." },
      { k: "reward_chronic", label: "Chronic care reward", type: "number",
        hint: "KES paid for chronic-care baskets (refill-heavy, higher LTV). Typical: 400." },
      { k: "reward_diagnostics", label: "Diagnostics reward", type: "number",
        hint: "KES paid for diagnostics / lab-testing orders. Typical: 300." },
      { k: "reward_teleconsult", label: "Teleconsult reward", type: "number",
        hint: "KES paid when the friend's first order is a teleconsult booking. Typical: 200." },
      { k: "min_payout_kes", label: "Minimum payout (KES)", type: "number",
        hint: "Smallest single M-Pesa disbursement. Below this the reward stays in the 'in-flight' bucket until it's cleared." },
    ],
  },
  {
    title: "Friend discount",
    description:
      "What the referred friend receives. In one-sided mode only the referrer earns; in two-sided the friend also gets a first-order discount.",
    fields: [
      { k: "two_sided_mode", label: "Two-sided mode (friend also gets discount)", type: "bool",
        hint: "ON = friend sees a promo code on the landing page and the invite copy mentions their discount. OFF = referrer-only rewards (cheaper, but lower friend-conversion)." },
      { k: "discount_percent", label: "Discount %", type: "number",
        hint: "Percent off the friend's first eligible order. Typical: 10. Ignored if two-sided mode is OFF." },
      { k: "discount_max_kes", label: "Max discount (KES)", type: "number",
        hint: "Hard ceiling on the friend discount regardless of basket size. Typical: 200." },
      { k: "code_expiry_days", label: "Promo code expiry (days)", type: "number",
        hint: "How long the friend has to redeem the assigned promo code before it expires. Typical: 14." },
      { k: "otp_required", label: "Require OTP on friend capture", type: "bool",
        hint: "Legacy knob. MYDAWA owns phone verification during signup, so the landing page no longer asks for OTP. Leave OFF unless you have a specific compliance reason." },
    ],
  },
  {
    title: "Payout eligibility",
    description:
      "The qualification rules a referral must pass before we schedule a payout. Prevents noise + outright fraud.",
    fields: [
      { k: "min_eligible_subtotal", label: "Min eligible order subtotal (KES)", type: "number",
        hint: "The friend's first order subtotal (non-POM items only) must meet or exceed this amount for the referrer to earn. Shown in the invite body." },
      { k: "aov_min_qualify_kes", label: "AOV floor to qualify for payout (KES)", type: "number",
        hint: "Alias for min subtotal — kept for backward compatibility with earlier dashboards. Set equal to min subtotal unless you have custom logic." },
      { k: "review_window_days", label: "Time to payout (days after delivery)", type: "number",
        hint: "Cool-off period after the friend's order is delivered, before the M-Pesa payout fires. Gives us time to detect refunds and chargebacks. Typical: 14." },
      { k: "referrer_tenure_orders", label: "Min referrer tenure (orders)", type: "number",
        hint: "Only customers with this many successful MYDAWA orders are invited to the program. Stops brand-new accounts from gaming invites. Typical: 2." },
      { k: "velocity_cap_30d", label: "Max successful referrals per 30d (velocity cap)", type: "number",
        hint: "Anti-fraud ceiling. If a referrer exceeds this in a rolling 30d window they're auto-paused for review. Typical: 20." },
    ],
  },
  {
    title: "Invites & automation",
    description:
      "When the system proactively reaches out on WhatsApp. These are the heartbeats that keep the program alive.",
    fields: [
      { k: "auto_invite_enabled", label: "Auto-send invites after delivery", type: "bool",
        hint: "Master switch for the hourly invite sweep. OFF = only manually triggered invites go out (useful during pause/audit)." },
      { k: "daily_summary_enabled", label: "Daily 24h summary broadcasts", type: "bool",
        hint: "ON = referrers who had activity in the last 24h get a WhatsApp digest at 8pm Nairobi time. Drives re-engagement." },
      { k: "invite_delay_hours", label: "Wait after delivery (hours)", type: "number",
        hint: "Only invite customers whose order was delivered at least this many hours ago. Avoids inviting people mid-delivery drama. Typical: 48." },
      { k: "invite_cooldown_days", label: "Invite cooldown (days)", type: "number",
        hint: "Minimum gap between successive invites to the same person, regardless of trigger (post-delivery, NPS, contacts upload). Typical: 45." },
      { k: "low_pool_threshold", label: "Low pool alert threshold", type: "number",
        hint: "When the available promo-code pool drops below this count, an alert fires on the Alerts page to prompt the team to upload more codes." },
    ],
  },
  {
    title: "Leaderboard & streak bonuses",
    description:
      "Gamification layer. Rewards super-sharers with bonus payouts and rank visibility on the WhatsApp LEADERBOARD command.",
    fields: [
      { k: "leaderboard_enabled", label: "Leaderboard enabled", type: "bool",
        hint: "Master switch. Turns on the WA LEADERBOARD command, RANK tracking, and streak-bonus payouts." },
      { k: "streak_threshold", label: "Streak bonus threshold (refs)", type: "number",
        hint: "Every N successful referrals inside the streak window triggers a bonus payout. Typical: 3." },
      { k: "streak_window_days", label: "Streak window (days)", type: "number",
        hint: "Rolling time window used to count streak progress. A referrer must hit N successful referrals within this window. Typical: 30." },
      { k: "streak_bonus_kes", label: "Streak bonus amount (KES)", type: "number",
        hint: "The one-shot bonus paid each time the referrer re-hits the threshold inside the window. Typical: 200." },
    ],
  },
  {
    title: "Daily budget circuit breaker",
    description:
      "Safety cap that protects Finance from runaway spend if something goes wrong (e.g. campaign goes viral). Zero = no cap.",
    fields: [
      { k: "daily_reward_budget_kes", label: "Daily reward budget (KES)", type: "number",
        hint: "Maximum KES we will schedule in referrer payouts per calendar day. Any approval beyond the cap is held and marked 'daily_budget_exhausted'." },
      { k: "daily_discount_budget_kes", label: "Daily discount budget (KES)", type: "number",
        hint: "Maximum KES of friend-discount face value per day. Informational for Finance; enforced at promo-pool level." },
    ],
  },
  {
    title: "Customer care & AI behaviour",
    description:
      "How the WhatsApp AI assistant handles off-topic questions — redirect to a human so we never answer medical queries.",
    fields: [
      { k: "customer_service_phone", label: "Customer care phone (for AI redirects)", type: "text",
        hint: "Shown in the AI redirect message when a user asks something unrelated to the program (e.g. prescription enquiries, delivery issues)." },
      { k: "ai_offtopic_redirect_enabled", label: "Redirect off-topic WA messages to CS phone", type: "bool",
        hint: "ON (recommended) = the AI always deflects non-program questions to the phone number above. OFF = the LLM answers freely (risky for medical queries)." },
    ],
  },
  {
    title: "A/B auto-winner promotion",
    description:
      "Automatic A/B testing of invite / notification copy. Two statistical methods run side-by-side: a strict z-test and an opt-in Bayesian sequential test. When either fires, losing variants are auto-disabled. For send-time routing you can additionally opt into a multi-armed bandit (Thompson sampling).",
    fields: [
      { k: "ab_auto_promote_enabled", label: "Auto-promote winning variants (disable losers)", type: "bool",
        hint: "Master switch for the nightly sweep. OFF = the sweep logs decisions but never pauses a variant (recommended during the first month while you watch the data)." },
      { k: "ab_min_sends_per_variant", label: "Z-test min sends per variant", type: "number",
        hint: "Minimum sample size BOTH arms must reach before the classic z-test is evaluated. Too low = noisy promotions. Typical: 500." },
      { k: "ab_significance_pct", label: "Z-test significance (90 / 95 / 99)", type: "number",
        hint: "Confidence level for the z-test. 95 is standard; 99 is stricter (fewer false positives, slower decisions)." },
      { k: "ab_sequential_enabled", label: "Sequential testing (early decisions)", type: "bool",
        hint: "ON = in addition to the z-test, run a Bayesian sequential test that can declare a winner at a lower sample size. Good for fast iteration; risk of early over-confidence." },
      { k: "ab_sequential_min_sends", label: "Sequential min sends per variant", type: "number",
        hint: "Floor below which the Bayesian test never fires even if the posterior probability is high. Typical: 50–100." },
      { k: "ab_sequential_prob_threshold", label: "Sequential P(winner) threshold (0–1)", type: "number", step: "0.01",
        hint: "How confident the posterior must be that arm A beats arm B for the Bayesian test to promote. Typical: 0.97 (very cautious), 0.95 (balanced)." },
      { k: "ab_bandit_enabled", label: "Multi-armed bandit (Thompson sampling) for send-time routing", type: "bool",
        hint: "ON = every message send picks a variant by sampling from each arm's Beta posterior (explore + exploit). Turns OFF the weighted-random routing. Recommended once you have ≥200 sends per variant." },
    ],
  },
];

function HelpPopover({ hint }) {
  if (!hint) return null;
  return (
    <span className="group relative inline-flex">
      <button
        type="button"
        tabIndex={0}
        aria-label="Field help"
        className="inline-flex cursor-help focus:outline-none focus-visible:ring-2 focus-visible:ring-[#66cc33] rounded-full"
      >
        <Info className="h-3.5 w-3.5 text-[#9eaf9b]" />
      </button>
      <span
        className="pointer-events-none absolute right-0 top-5 z-20 hidden w-64 rounded-lg border border-[#e2e8e2] bg-white p-3 text-[11px] leading-relaxed text-[#1a1c19] shadow-lg group-hover:block group-focus-within:block"
        role="tooltip"
      >
        {hint}
      </span>
    </span>
  );
}

export default function ProgramConfig() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [toast, setToast] = useState("");

  useEffect(() => { (async () => {
    try {
      const { data } = await api.get("/program/config");
      setCfg(data);
    } catch (e) {
      setToast(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  })(); }, []);

  const set = (k, v) => {
    setCfg((c) => ({ ...c, [k]: v }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    const patch = {};
    SECTIONS.forEach((s) => s.fields.forEach((f) => { patch[f.k] = cfg[f.k]; }));
    try {
      const { data } = await api.put("/program/config", patch);
      setCfg(data);
      setDirty(false);
      setToast("Program config saved");
      setTimeout(() => setToast(""), 3000);
    } catch (e) {
      setToast(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex h-60 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#66cc33]" /></div>;
  if (!cfg) return <div className="text-sm text-[#c01974]">Could not load program config.</div>;

  // Slugify section titles for data-testid purposes: keep alphanum only,
  // collapse runs of anything else to a single dash. Avoids literal `&` and
  // `/` leaking into selectors.
  const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-[#1a1c19] sm:text-3xl flex items-center gap-2">
            <Settings className="h-6 w-6 text-[#66cc33]" /> Program Config
          </h1>
          <p className="text-sm text-[#5c665a]">
            MYDAWA Refer &amp; Earn is an <b>always-on</b> automated program. Tune every lever
            below — changes take effect immediately across invites, rewards, AI replies, and payouts.
            Hover the <Info className="inline h-3 w-3 text-[#9eaf9b]" /> icon on any field for a short
            explanation of what it controls and the typical value.
          </p>
        </div>
        <button className="admin-btn" onClick={save} disabled={saving || !dirty} data-testid="program-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </button>
      </div>

      {toast && <div className="rounded-lg bg-[#eaf8de] px-4 py-2 text-sm font-semibold text-[#3f7c21]" data-testid="program-toast">{toast}</div>}

      <div className="grid gap-5 lg:grid-cols-2">
        {SECTIONS.map((s) => (
          <div key={s.title} className="admin-card" data-testid={`program-section-${slug(s.title)}`}>
            <h3 className="text-sm font-bold uppercase tracking-wider text-[#5c665a]">{s.title}</h3>
            {s.description && (
              <p className="mt-1 mb-4 text-xs leading-relaxed text-[#5c665a]" data-testid={`program-description-${slug(s.title)}`}>
                {s.description}
              </p>
            )}
            <div className="space-y-3">
              {s.fields.map((f) => (
                <div key={f.k} className="border-b border-[#f0f3f0] pb-3 last:border-b-0 last:pb-0">
                  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5">
                    <label className="flex min-w-0 flex-1 basis-[60%] items-center gap-1.5 pr-2 text-sm text-[#1a1c19]" htmlFor={`fld-${f.k}`}>
                      <span className="break-words">{f.label}</span>
                      <HelpPopover hint={f.hint} />
                    </label>
                    {f.type === "bool" ? (
                      <input
                        id={`fld-${f.k}`}
                        type="checkbox"
                        checked={!!cfg[f.k]}
                        onChange={(e) => set(f.k, e.target.checked)}
                        className="h-5 w-5 shrink-0 accent-[#66cc33]"
                        data-testid={`program-${f.k}`}
                      />
                    ) : (
                      <input
                        id={`fld-${f.k}`}
                        type={f.type}
                        step={f.step || undefined}
                        value={cfg[f.k] ?? ""}
                        onChange={(e) => set(f.k, f.type === "number" ? Number(e.target.value) : e.target.value)}
                        className="admin-input w-28 shrink-0 text-right"
                        data-testid={`program-${f.k}`}
                      />
                    )}
                  </div>
                  {f.hint && (
                    <p
                      id={`hint-${f.k}`}
                      data-testid={`program-hint-${f.k}`}
                      className="sr-only"
                    >
                      {f.hint}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
